import {
  Component,
  Input,
  HostBinding,
  Output,
  EventEmitter
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { RadioChange } from './radio-change.class';


@Component({
  selector: 'suka-radio',
  template: `
    <input
      *ngIf="!skeleton"
      class="radio-button"
      type="radio"
      [checked]="checked"
      [disabled]="disabled"
      [name]="name"
      [id]="id"
      [required]="required"
      [value]="value"
      [attr.aria-labelledby]="ariaLabelledby"
      (change)="onChange($event)">
    <div *ngIf="skeleton" class="radio-button skeleton"></div>
    <label
      class="radio-button__label"
      [ngClass]="{
        'skeleton': skeleton
      }"
      [for]="id"
      id="label-{{id}}">
      <span class="radio-button__appearance"></span>
      <ng-content></ng-content>
    </label>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: Radio,
      multi: true
    }
  ]
})
export class Radio {
  /**
   * Used to dynamically create unique ids for the `Radio`.
   */
  static radioCount = 0;

  /** Sets the checked status. */
  @Input() checked = false;

  /** Sets the `Radio` `<input>` name. */
  @Input() name = '';

  /** Set to `true` to disable the `Radio`. */
  @Input() disabled = false;

  /** Sets the label placement. Default to `right`. */
  @Input() labelPlacement: 'right' | 'left' = 'right';

  @Input() set ariaLabelledby(value: string) {
    this._labelledby = value;
  }

  get ariaLabelledby() {
    if (this._labelledby) {
      return this._labelledby;
    }
    return `label-${this.id}`;
  }
  /**
   * Sets the HTML required attribute
   */
  @Input() required = false;
  /**
   * The value of the `Radio`.
   */
  @Input() value = '';
  /**
   * Set to `true` for a loading radio.
   */
  @Input() skeleton = false;
  /**
   * The id for the `Radio`.
   */
  @Input() id = `radio-${Radio.radioCount++}`;
  /**
   * emits when the state of the radio changes
   */
  // tslint:disable-next-line: no-output-native
  @Output() change = new EventEmitter<RadioChange>();
  /**
   * Binds 'radio' value to the role attribute for `Radio`.
   */
  @HostBinding('attr.role') role = 'radio';

  @HostBinding('class.radio-button-wrapper') hostClass = true;

  @HostBinding('class.radio-button-wrapper--label-left') get labelLeft() {
    return this.labelPlacement === 'left';
  }

  // tslint:disable-next-line: variable-name
  protected _labelledby = '';

  /**
   * Handler provided by the `RadioGroup` to bubble events up
   */
  radioChangeHandler = (event: RadioChange) => { };

  /**
   * Synchronizes with the `RadioGroup` in the event of a changed `Radio`.
   * Emits the changes of both the `RadioGroup` and `Radio`.
   */
  onChange(event: Event) {
    event.stopPropagation();
    this.checked = (event.target as HTMLInputElement).checked;
    const radioEvent = new RadioChange(this, this.value);
    this.change.emit(radioEvent);
    this.radioChangeHandler(radioEvent);
  }

  /**
   * Method called by `RadioGroup` with a callback function to bubble `RadioChange` events
   * @param fn callback that expects a `RadioChange` as an argument
   */
  registerRadioChangeHandler(fn: (event: RadioChange) => void) {
    this.radioChangeHandler = fn;
  }
}
